import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { APISetting } from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import { gsap } from "gsap";

import { EventsManager, ReelsEvents, WinEvents } from "../../../abstracts/events/EventsManager";
import { AbstractFlowPresentation } from "../../../abstracts/flow/AbstractFlowStatePresentation";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { playSound, stopSound } from "../../../abstracts/utils/SoundUtils";
import { normalConfig } from "../../UCConfig";
import { UgliestCatch } from "../../UgliestCatch";
import { UCSoundNames } from "../../assetsLoader/UCAssetsNames";
import UCReel from "../../modules/Reels/UCReel";
import { UCReelsView } from "../../modules/Reels/UCReelsView";
import { UCBigFishSymbol } from "../../modules/Reels/symbols/UCBigFishSymbol";
import { UCSymbol } from "../../modules/Reels/symbols/UCSymbol";
import { UCWildSymbol } from "../../modules/Reels/symbols/UCWildSymbol";
import { UCWinView } from "../../modules/Winnings/UCWinView";
import { UCSpecialSymbolNames, UCSymbolNames } from "../../types/UCEnums";
import { UCResultDecorator } from "../decorators/UCResultDecorators";

export class UCNormalResultPresentation extends AbstractFlowPresentation {
    private reelsView: UCReelsView;
    private winView: UCWinView;
    private ucReels: UCReel[] = [];
    private previousOpenedReels: string = ``;

    constructor(id: string = `UCNormalResultPresentation`) {
        super(id);
    }

    protected handleEvents(): void {
        super.handleEvents();

        EventsManager.on(ReelsEvents.GET_REELS, (ucReels: UCReel[]) => {
            this.ucReels = ucReels;
        });

        EventsManager.on(ReelsEvents.GET_REELS_VIEW, (view: UCReelsView) => {
            this.reelsView = view;
        });

        EventsManager.on(WinEvents.GET_WINNINGS_VIEW, (view: UCWinView) => {
            this.winView = view;
        });
    }

    protected onDefaultStart() {
        super.onDefaultStart();

        const padding = UgliestCatch.gameFlowController.calculatePaddingDuration(
            this.flowPresentationTimeline.totalDuration()
        );

        if (padding > 0) {
            gsap.delayedCall(padding, this.handleFinishAnimation.bind(this));
        } else {
            this.handleFinishAnimation();
        }
    }

    private handleFinishAnimation(): void {
        this.flowPresentationTimeline = gsap.timeline({
            onStart: () => {},
        });

        ////////////////////////

        const stopDelay = UgliestCatch.apiPlugin.settings.get(APISetting.FAST_SPIN, false)
            ? normalConfig.skipReelsDelay
            : normalConfig.stopSpinReelDelay;

        UCResultDecorator({
            scatterSymbolName: UCSymbolNames.SCATTER,
            finalDataReels: this.model.gameData.reels,
            restrictedReelsByIndex: [0, 1],
            gameReels: this.ucReels,
            timeline: this.flowPresentationTimeline,
            stopSpinDelay: stopDelay,
            showNearWinAnimationDelay: 0,
            nearWinDuration: normalConfig.nearWinDuration,
            hideNearWinDelay: normalConfig.hideNearWinDelay,
            wasFeatureBuy: this.model.gameData.wasFeatureBuy || this.model.data.enhancedBet,
            scatterLandingSoundName: `scatterLanding`,
            playSoundDelay: 0.18,
            onDecoratorComplete: this.handleMysteryRevealAnimation.bind(this),
            gameMode: this.model.data.mode,
        });

        if (SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
            this.skip();
        }
    }

    protected handleMysteryRevealAnimation() {
        const onMysteryAnimationLabel = `onMysteryAnimationLabel`;

        this.flowPresentationTimeline = gsap.timeline({
            paused: true,
            onComplete: () => {
                this.handleWild();
            },
        });
        this.flowPresentationTimeline.data = { name: `mysteryRevealTimeline` };

        this.flowPresentationTimeline.addLabel(onMysteryAnimationLabel);

        if (UgliestCatch.model.mysteryMap.includes(true)) {
            this.model.data.reels.forEach((reel: string[], reelIndex: number) => {
                if (UgliestCatch.model.mysteryMap[reelIndex]) {
                    this.flowPresentationTimeline.add(
                        this.ucReels[reelIndex].getMysteryAnimation(
                            this.model.data.symbolSizes[reelIndex],
                            this.model.data.evaluatedArea[reelIndex]
                        ),
                        onMysteryAnimationLabel
                    );
                }
            });
            playSound({ name: UCSoundNames.XWAYS_EXPAND });
        }

        this.flowPresentationTimeline.addLabel(`onMysteryAnimationLabelEnd`, `>-=0.2`);

        this.addSkip(this.flowPresentationTimeline);

        this.flowPresentationTimeline.resume();
    }

    protected override finishPresentation() {
        if (this.model.data.nextMode === AbstractGameMode.NORMAL) {
            this.previousOpenedReels = ``;
        }

        if (
            this.model.gameData.addedNumberOfFreespinsThisSpin > 0 &&
            this.model.gameData.mode !== AbstractGameMode.NORMAL &&
            !this.model.gameData.brokeTheBank
        ) {
            const finishTimeline = gsap.timeline({
                onComplete: () => {
                    if (this.model.data.nextMode !== AbstractGameMode.NORMAL) {
                        this.keypad.setZeroBetSpinCounter(this.model.data.freespinsLeft);
                    }

                    super.finishPresentation();
                },
            });

            const showSpinsWonAnimation = this.winView.showSpinsWinScreen(
                this.model.gameData.addedNumberOfFreespinsThisSpin
            );
            finishTimeline.call(() => {
                this.controller.setSkipCallback(() => {
                    finishTimeline.timeScale(3);
                });
            });
            finishTimeline.add(showSpinsWonAnimation);
        } else {
            super.finishPresentation();
        }
    }

    protected addSkip(timeline: gsap.core.Timeline, callback: Function | null = null): void {
        const skipToNextLevel = () => {
            this.stopSoundsOnSkip();

            const nextLabel: string = timeline.nextLabel();

            timeline.seek(timeline.labels[nextLabel], false);

            callback !== null && callback();
        };

        this.controller?.setSkipCallback(skipToNextLevel);
    }

    protected handleWild() {
        this.initializeTimeline();

        const allVisibleSymbols = this.ucReels.map((reel) => reel.getAllVisibleStopSymbols());

        const collectTimeline = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
            paused: true,
            onComplete: () => {
                this.flowPresentationTimeline.resume();
            },
        });
        collectTimeline.data = { name: `collectTimeline` };

        const revealBigFishTimeline = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
            paused: true,
            onComplete: () => {
                collectTimeline.resume();
            },
        });
        revealBigFishTimeline.data = { name: `revealBigFishTimeline` };

        const activationTimeline = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
            onComplete: () => {
                revealBigFishTimeline.resume();
            },
        });
        activationTimeline.data = { name: `activationTimeline` };

        const onWildActivationLabel = `onWildActivationLabel`;

        this.flowPresentationTimeline.addLabel(onWildActivationLabel);

        if (UgliestCatch.model.hasFishesToCollect) {
            UgliestCatch.model.fishesToCollect.forEach((fishReel, fishReelIndex) => {
                fishReel.forEach((hasFishToCollect, fishToCollectIndex) => {
                    if (hasFishToCollect) {
                        const fishSymbol: UCSymbol = allVisibleSymbols[fishReelIndex][
                            fishToCollectIndex
                        ] as UCSymbol;

                        if (fishSymbol.name === UCSpecialSymbolNames.BIG_FISH) {
                            revealBigFishTimeline.call(() => {
                                this.addSkip(revealBigFishTimeline, () => {
                                    stopSound({ name: UCSoundNames.BIG_FISH_SPIN });
                                    playSound({ name: UCSoundNames.BIG_FISH_ABORD });
                                });
                            }, undefined);

                            revealBigFishTimeline.add(
                                (fishSymbol as UCBigFishSymbol).revealFish(
                                    this.model.data.evaluatedArea[fishReelIndex][fishToCollectIndex]
                                ),
                                `<`
                            );

                            revealBigFishTimeline.addLabel(
                                `revealBigFishLabel_${fishReelIndex}_${fishToCollectIndex}`,
                                `>-0.2`
                            );
                        }
                    }
                });
            });

            const wildsToBeMultiplied: UCWildSymbol[] = [];

            UgliestCatch.model.activeWildsPositions.forEach(
                (reel: boolean[], reelIndex: number) => {
                    if (reel.includes(true)) {
                        reel.forEach((activeWild: boolean, symbolIndex: number) => {
                            if (activeWild) {
                                const wildSymbol = allVisibleSymbols[reelIndex][
                                    symbolIndex
                                ] as UCWildSymbol;
                                const activationAnimation = wildSymbol.getActivationAnimation();

                                activationTimeline.add(activationAnimation, 0);

                                collectTimeline.call(() => {
                                    this.addSkip(collectTimeline);
                                });

                                UgliestCatch.model.fishesToCollect.forEach(
                                    (fishReel, fishReelIndex) => {
                                        fishReel.forEach((hasFishToCollect, fishToCollectIndex) => {
                                            if (hasFishToCollect) {
                                                const fishSymbol: UCSymbol = allVisibleSymbols[
                                                    fishReelIndex
                                                ][fishToCollectIndex] as UCSymbol;

                                                collectTimeline.add(
                                                    this.reelsView.catchFish(
                                                        fishSymbol,
                                                        wildSymbol,
                                                        fishReelIndex
                                                    )
                                                );
                                            }
                                        });
                                    }
                                );
                                const catchFishLabel = `catchFish_${reelIndex}_${symbolIndex}`;
                                collectTimeline.addLabel(catchFishLabel, `>-0.2`);

                                if (this.model.data.mode !== AbstractGameMode.NORMAL) {
                                    collectTimeline.call(() => {
                                        this.addSkip(collectTimeline);
                                    });
                                    collectTimeline.add(
                                        this.reelsView.getBoatProgressionAnimation(
                                            this.model.data.fishermanProgression,
                                            false,
                                            wildSymbol
                                        )
                                    );

                                    const boatProgressionLabel = `boatProgressionLabel_${reelIndex}_${symbolIndex}`;
                                    collectTimeline.addLabel(boatProgressionLabel, `>-0.2`);

                                    wildsToBeMultiplied.push(wildSymbol);
                                }
                            }
                        });
                    }
                }
            );

            if (wildsToBeMultiplied.length > 0) {
                this.reelsView.handleBoatToWildMultiplyAnimation(
                    wildsToBeMultiplied,
                    UgliestCatch.model.data.fsMultiplier,
                    this.addSkip.bind(this),
                    collectTimeline
                );
            }
        } else {
            if (this.model.data.mode !== AbstractGameMode.NORMAL) {
                this.model.data.reels.forEach((reel: string[], reelIndex: number) => {
                    reel.forEach((symbolName, symbolIndex: number) => {
                        if (
                            (UgliestCatch.model.data.extraPositionsOpen[reelIndex] &&
                                symbolName === UCSpecialSymbolNames.WILD) ||
                            (!UgliestCatch.model.data.extraPositionsOpen[reelIndex] &&
                                symbolName === UCSpecialSymbolNames.WILD &&
                                symbolIndex > 0 &&
                                symbolIndex < 4)
                        ) {
                            const wildSymbol = allVisibleSymbols[reelIndex][
                                symbolIndex
                            ] as UCWildSymbol;

                            collectTimeline.add(
                                this.reelsView.getBoatProgressionAnimation(
                                    this.model.data.fishermanProgression,
                                    false,
                                    wildSymbol
                                )
                            );
                        }
                    });
                });
            }
        }
    }

    protected skip(): void {
        super.skip();

        this.flowPresentationTimeline.timeScale(3);
    }

    protected stopSoundsOnSkip() {
        stopSound({ name: UCSoundNames.WILD_CATCH_FISH_01 });
        stopSound({ name: UCSoundNames.WILD_CATCH_FISH_02 });
        stopSound({ name: UCSoundNames.WILD_CATCH_FISH_03 });
        stopSound({ name: UCSoundNames.WILD_GOLD_COUNTER });
    }
}
